import { useApi } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";

export interface ComboboxOption {
  label: string;
  value: string;
}

export interface ServiceCategory {
  id: number;
  name: string;
  slug: string;
}

export interface ServiceCategoryWithSubcategories extends ServiceCategory {
  subcategories: ServiceCategory[];
}

export function useCategories() {
  const api = useApi();

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['public', 'categories', 'with-subcategories'],
    queryFn: async () => {
      return await api.public.categories.getCategoriesWithSubcategories({});
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - categories rarely change
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Raw categories with subcategories
  const categoriesWithSubcategories = computed<ServiceCategoryWithSubcategories[]>(() => {
    return data.value || [];
  });

  // Transform categories to combobox-compatible format (value/label)
  const categoryOptions = computed<ComboboxOption[]>(() => {
    return categoriesWithSubcategories.value.map((category) => ({
      value: category.id.toString(),
      label: category.name,
    }));
  });

  // Transform all subcategories from all categories to combobox-compatible format
  const subcategoryOptions = computed<ComboboxOption[]>(() => {
    const allSubcategories: ComboboxOption[] = [];
    for (const category of categoriesWithSubcategories.value) {
      for (const subcategory of category.subcategories) {
        allSubcategories.push({
          value: subcategory.id.toString(),
          label: subcategory.name,
        });
      }
    }
    return allSubcategories;
  });

  // Maps for quick lookup by ID (for displaying existing selections)
  const categoryById = computed<Record<number, ServiceCategory>>(() => {
    const map: Record<number, ServiceCategory> = {};
    for (const category of categoriesWithSubcategories.value) {
      map[category.id] = { id: category.id, name: category.name, slug: category.slug };
    }
    return map;
  });

  const subcategoryById = computed<Record<number, ServiceCategory>>(() => {
    const map: Record<number, ServiceCategory> = {};
    for (const category of categoriesWithSubcategories.value) {
      for (const subcategory of category.subcategories) {
        map[subcategory.id] = subcategory;
      }
    }
    return map;
  });

  return {
    categoriesWithSubcategories,
    categoryOptions,
    subcategoryOptions,
    categoryById,
    subcategoryById,
    isLoading,
    error,
  };
}

export default useCategories;

